/**
 * ComparisonAdminPanel — Generate and manage Crown & Rose vs [competitor] pages.
 *
 * Each comparison page is LLM-generated, AEO/SEO-optimised, and served
 * at the public route /compare/{slug}.
 *
 * Admin → Operations → Comparison Pages tab
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Loader2, Sparkles, ExternalLink, RefreshCw, CheckCircle2,
  Globe, Clock, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import { comparisonApi } from '@/lib/api';

const COMPETITOR_NAMES = {
  scrivener:      'Scrivener',
  atticus:        'Atticus',
  vellum:         'Vellum',
  prowritingaid:  'ProWritingAid',
  jasper:         'Jasper',
};

function CompetitorRow({ competitor, onGenerate, generating }) {
  const isGen = generating === competitor.key;
  const BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/api$/, '') || '';
  return (
    <div
      data-testid={`comparison-row-${competitor.key}`}
      className="flex items-center gap-3 rounded-md border border-border p-3 bg-card"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-[13px]">Crown & Rose vs {competitor.name}</span>
          {competitor.generated ? (
            <Badge className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 border-0">
              <CheckCircle2 className="h-3 w-3 mr-0.5" /> Live
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">Not generated</Badge>
          )}
        </div>
        {competitor.generated && (
          <div className="flex items-center gap-1 mt-0.5 text-[11px] text-muted-foreground">
            <Globe className="h-3 w-3" />
            <span className="font-mono">/compare/{competitor.slug}</span>
            <a
              href={`${BASE_URL}/compare/${competitor.slug}`}
              target="_blank"
              rel="noreferrer"
              className="ml-1 text-primary hover:underline flex items-center gap-0.5"
            >
              Preview <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        )}
        {competitor.generated_at && (
          <div className="flex items-center gap-1 mt-0.5 text-[10.5px] text-muted-foreground">
            <Clock className="h-2.5 w-2.5" />
            Generated {new Date(competitor.generated_at).toLocaleDateString()}
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant={competitor.generated ? 'outline' : 'default'}
        className="gap-1.5 flex-shrink-0"
        disabled={isGen}
        onClick={() => onGenerate(competitor.key)}
        data-testid={`comparison-generate-${competitor.key}`}
      >
        {isGen
          ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
          : competitor.generated
            ? <><RefreshCw className="h-3.5 w-3.5" /> Regenerate</>
            : <><Sparkles className="h-3.5 w-3.5" /> Generate</>}
      </Button>
    </div>
  );
}

export default function ComparisonAdminPanel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(null); // competitor key being generated

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await comparisonApi.adminList());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async (key) => {
    setGenerating(key);
    try {
      await comparisonApi.generate({ competitor_key: key });
      toast.success(`Crown & Rose vs ${COMPETITOR_NAMES[key] || key} page generated!`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Generation failed — please retry.';
      toast.error(msg);
    } finally {
      setGenerating(null);
    }
  };

  const competitors = data?.competitors || [];
  const generatedMap = Object.fromEntries(
    (data?.pages || []).map((p) => [p.slug, p])
  );

  const rows = competitors.map((c) => {
    const page = generatedMap[c.slug];
    return {
      ...c,
      generated:    !!page,
      generated_at: page?.generated_at,
    };
  });

  const generatedCount = rows.filter((r) => r.generated).length;

  return (
    <div className="space-y-4" data-testid="comparison-admin-panel">
      <PanelHeader
        title="Comparison Landing Pages"
        subtitle="AEO-optimised 'Crown & Rose vs competitor' pages. Each page is LLM-generated and served at /compare/{slug}."
        onRefresh={load}
      />

      {/* Progress summary */}
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-4 py-3">
        <div className={`h-2 w-2 rounded-full ${generatedCount > 0 ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
        <span className="text-[12.5px]">
          <strong>{generatedCount}</strong> of <strong>{rows.length}</strong> comparison pages generated
        </span>
        {generatedCount === 0 && (
          <span className="ml-auto text-[11.5px] text-amber-600 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            Generate all 5 pages to maximise AEO coverage
          </span>
        )}
        {generatedCount > 0 && generatedCount < rows.length && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto gap-1.5 text-[11.5px] h-7"
            disabled={!!generating}
            onClick={async () => {
              for (const row of rows.filter((r) => !r.generated)) {
                await handleGenerate(row.key);
              }
            }}
            data-testid="comparison-generate-missing"
          >
            <Sparkles className="h-3 w-3" /> Generate missing ({rows.length - generatedCount})
          </Button>
        )}
        {generatedCount === 0 && rows.length > 0 && (
          <Button
            size="sm"
            className="ml-auto gap-1.5 text-[11.5px] h-7"
            disabled={!!generating}
            onClick={async () => {
              for (const row of rows) {
                await handleGenerate(row.key);
              }
            }}
            data-testid="comparison-generate-all"
          >
            <Sparkles className="h-3 w-3" /> Generate all 5
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-[13px] py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <CompetitorRow
              key={c.key}
              competitor={c}
              onGenerate={handleGenerate}
              generating={generating}
            />
          ))}
        </div>
      )}

      <div className="text-[11px] text-muted-foreground bg-muted/30 rounded-md px-3 py-2">
        Each page includes: full comparison table, pros/cons lists, 5 FAQs, structured data, and a CTA section.
        Pages are served at <strong>/compare/{`{slug}`}</strong> with full hreflang SEO.
        Regenerating overwrites the existing page.
      </div>
    </div>
  );
}
